import { useEffect, useRef, useState, useCallback } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getAuth } from 'firebase/auth';

const WS_BASE = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL?.replace(/\/v1$/, '') ?? 'http://localhost:3001';

async function getToken(): Promise<string | null> {
  try {
    const user = getAuth().currentUser;
    if (user) return await user.getIdToken();
  } catch { /* not signed in */ }
  return null;
}

export type PercentileStats = {
  p5: number; p50: number; p95: number; avg: number; count: number;
};

export type ModelTokenStats = {
  provider: string;
  modelName: string;
  totalTokens: number;
  totalCost: number;
  requests: number;
  latency: PercentileStats;
};

export type TenantActivity = {
  tenantId: string;
  tenantName: string;
  openConversations: number;
  messagesLastHour: number;
  tokensLastHour: number;
  pendingHandoffs: number;
  pendingApprovals: number;
};

export type RecentError = {
  id: string;
  tenantId: string;
  tenantName?: string;
  conversationId: string | null;
  error: string;
  context: unknown;
  createdAt: string;
};

export type PlatformSnapshot = {
  capturedAt: string;
  openConversations: number;
  activeAgents: number;
  pendingHandoffs: number;
  pendingApprovals: number;
  messagesLastHour: number;
  conversationsStartedLastHour: number;
  handoffsLastHour: number;
  totalTokensLast24h: number;
  totalCostLast24h: number;
  tokensByModel: ModelTokenStats[];
  latencyOverall: PercentileStats;
  tenants: TenantActivity[];
  recentErrors: RecentError[];
};

export type ObsStatus = 'connecting' | 'live' | 'error' | 'disconnected';

export function useObservabilitySocket() {
  const [snapshot, setSnapshot] = useState<PlatformSnapshot | null>(null);
  const [status, setStatus] = useState<ObsStatus>('connecting');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const socketRef = useRef<Socket | null>(null);

  const connect = useCallback(async () => {
    const token = await getToken();
    if (!token) { setStatus('error'); return; }

    const socket = io(`${WS_BASE}/observability`, {
      auth: { token },
      transports: ['websocket'],
      reconnectionDelay: 3000,
      reconnectionAttempts: 20,
    });

    socketRef.current = socket;

    socket.on('connect', () => setStatus('live'));
    socket.on('disconnect', () => setStatus('disconnected'));
    socket.on('connect_error', () => setStatus('error'));

    const onData = (data: PlatformSnapshot) => {
      setSnapshot(data);
      setLastUpdated(new Date());
      setStatus('live');
    };

    socket.on('platform:snapshot', onData);
    socket.on('platform:update', onData);
  }, []);

  useEffect(() => {
    void connect();
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [connect]);

  return { snapshot, status, lastUpdated };
}
